import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getCountryNames, countryIdByName } from '@/lib/geoApi';
import { SelectField } from '@/components/ui/SelectField';

// ────────────────────────────────────────────────────────────────────────────
// Public lead-intake form — NO authentication required.
//
// URL: /lead-form/:tenantId        e.g. /lead-form/cmnq7rwu50000f86uu5xocpol
//      /lead-form?tenant=<id>      query-param form for easy bookmarking
//
// Use cases:
//   1. Embed this page (or its iframe) on a tenant's marketing site so
//      website visitors can drop leads straight into the CRM.
//   2. QA tool — paste the URL into any browser, fill the form, watch the
//      lead show up in the dashboard with the right auto-assign rule
//      applied. Lets us verify counselor-rotation, country gating,
//      daily-cap behaviour etc. without scripting against the API.
//
// We bypass the shared `api` client because that one auto-attaches the
// JWT and runs through interceptors meant for the dashboard. Public form
// posts go via a plain axios instance so the request is genuinely
// anonymous (matches what an embedded form on a stranger's site would do).
// ────────────────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : import.meta.env.DEV
    ? 'http://localhost:3000/api'
    : '/api';

interface TenantMeta {
  tenantId: string;
  organizationName: string;
  logoSquare: string | null;
  logoRectangular: string | null;
  primaryColor: string | null;
}

interface SubmitResult {
  ok: boolean;
  leadId: string | null;
  assignedToName: string | null;
  mergedIntoExisting: boolean;
  /** Reasons each LeadAssignRule was skipped — used to explain why no
      counselor got the lead. Visible only on the QA / test surface. */
  autoAssignSkipReasons?: string[];
  unassigned?: boolean;
}

export function PublicLeadFormPage() {
  const params = useParams<{ tenantId?: string }>();
  const [searchParams] = useSearchParams();
  const tenantId = params.tenantId || searchParams.get('tenant') || '';

  // Meta load — drives the header (organization name / logo) + the
  // "Form not found" guard. We deliberately return 404 from the backend
  // when the tenant doesn't exist so probes can't enumerate tenant IDs.
  const [meta, setMeta] = useState<TenantMeta | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [metaLoading, setMetaLoading] = useState(true);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    source: 'Web Form',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<SubmitResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [countries, setCountries] = useState<string[]>([]);

  // Countries from our own DB geo master (public endpoint).
  useEffect(() => {
    getCountryNames().then(setCountries).catch(() => {});
  }, []);

  useEffect(() => {
    if (!tenantId) {
      setMetaError('Missing tenant id in URL');
      setMetaLoading(false);
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    axios
      .get<TenantMeta>(`${API_BASE}/public/lead-intake/${tenantId}`)
      .then((r) => {
        if (cancelled) return;
        setMeta(r.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setMetaError(
          err?.response?.status === 404
            ? 'This form is no longer available.'
            : 'Could not load the form. Please try again.',
        );
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const update = <K extends keyof typeof form>(key: K, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!form.name.trim()) return setSubmitError('Please enter your name.');
    if (!form.phone.trim() && !form.email.trim())
      return setSubmitError('Provide a phone number or email so we can contact you.');

    setSubmitting(true);
    try {
      // utm_* attribution from the URL — if a marketing page embeds this
      // form behind a campaign link, we keep the tracking intact.
      const utmSource = searchParams.get('utm_source') ?? undefined;
      const utmMedium = searchParams.get('utm_medium') ?? undefined;
      const utmCampaign = searchParams.get('utm_campaign') ?? undefined;

      const { data } = await axios.post<SubmitResult>(`${API_BASE}/public/lead-intake`, {
        tenantId,
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        country: form.country || undefined,
        countryId: countryIdByName(form.country) ?? undefined,
        source: form.source || 'Web Form',
        utmSource,
        utmMedium,
        utmCampaign,
      });
      setSubmitted(data);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ||
          'Could not submit. Please try again in a moment.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────
  if (metaLoading) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          <p className="mt-3 text-sm text-slate-500">Loading form…</p>
        </div>
      </Shell>
    );
  }
  if (metaError || !meta) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <AlertCircle className="h-6 w-6 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">
            Form unavailable
          </h2>
          <p className="mt-2 text-sm text-slate-500 max-w-sm">
            {metaError ?? 'The link looks broken. Please check with whoever shared it.'}
          </p>
        </div>
      </Shell>
    );
  }

  // ── Success state ─────────────────────────────────────────────────────
  if (submitted) {
    return (
      <Shell brand={meta}>
        <div className="flex flex-col items-center text-center py-12">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
            <CheckCircle2 className="h-7 w-7 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Thanks!</h2>
          <p className="mt-3 text-sm text-slate-600 max-w-md">
            Your details are in. {submitted.assignedToName ? (
              <>
                Our team member{' '}
                <span className="font-semibold text-slate-900">
                  {submitted.assignedToName}
                </span>{' '}
                will reach out shortly.
              </>
            ) : submitted.mergedIntoExisting ? (
              <>
                We already had your details — your assigned counselor will follow
                up using the existing record.
              </>
            ) : (
              <>Our team will reach out shortly.</>
            )}
          </p>

          {/* QA diagnostic — only shown when the lead landed unassigned,
              so the admin testing the form sees exactly which constraint
              skipped each rule. Hidden in the normal "got assigned"
              flow so it doesn't leak counselor schedules to website
              visitors. */}
          {submitted.unassigned && (submitted.autoAssignSkipReasons?.length ?? 0) > 0 && (
            <div className="mt-6 w-full max-w-md text-left rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">
                ⚠️ Lead created but unassigned — diagnostic
              </p>
              <ul className="text-xs text-amber-700 space-y-1 list-disc pl-5">
                {submitted.autoAssignSkipReasons!.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            onClick={() => {
              setSubmitted(null);
              setForm({
                name: '',
                email: '',
                phone: '',
                country: '',
                source: 'Web Form',
                message: '',
              });
            }}
            className="mt-8 text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            Submit another lead
          </button>
        </div>
      </Shell>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────
  return (
    <Shell brand={meta}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <Field label="Your name" required>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Rahul Sharma"
            className={INPUT_CLASS}
            required
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              placeholder="+91 98765 43210"
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              placeholder="rahul@example.com"
              className={INPUT_CLASS}
            />
          </Field>
        </div>
        <p className="text-xs text-slate-400 -mt-2">
          Provide at least one — phone or email.
        </p>

        <Field label="Country">
          <SelectField
            value={form.country}
            onChange={(e) => update('country', e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="">Select country…</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SelectField>
        </Field>

        <Field label="How did you hear about us?">
          <SelectField
            value={form.source}
            onChange={(e) => update('source', e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="Web Form">Web Form</option>
            <option value="Referral">Referral</option>
            <option value="Google Ads">Google Ads</option>
            <option value="Facebook Ads">Facebook Ads</option>
            <option value="Instagram">Instagram</option>
            <option value="LinkedIn">LinkedIn</option>
            <option value="Other">Other</option>
          </SelectField>
        </Field>

        {submitError && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold py-3 text-sm shadow-md shadow-indigo-200/50 transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting…
            </>
          ) : (
            'Submit'
          )}
        </button>

        <p className="text-[11px] text-slate-400 text-center pt-1">
          By submitting, you agree to be contacted by {meta.organizationName}.{' '}
          <Link to="/privacy" className="underline">
            Privacy policy
          </Link>
          .
        </p>
      </form>
    </Shell>
  );
}

// ── Layout shell ────────────────────────────────────────────────────────────
// Standalone — does NOT use DashboardLayout (that one requires auth +
// renders the sidebar). This page is purposely lightweight so it can be
// dropped into an iframe on a tenant's website.

function Shell({
  children,
  brand,
}: {
  children: React.ReactNode;
  brand?: TenantMeta;
}) {
  const primary = brand?.primaryColor ?? '#6366F1';
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          {brand?.logoRectangular || brand?.logoSquare ? (
            <img
              src={brand.logoRectangular || brand.logoSquare!}
              alt={brand.organizationName}
              className="h-10 mx-auto object-contain"
            />
          ) : (
            <h1
              className="text-2xl font-bold"
              style={{ color: primary }}
            >
              {brand?.organizationName ?? 'Get in touch'}
            </h1>
          )}
          <p className="mt-2 text-sm text-slate-500">
            Drop your details and we'll get back to you.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Field wrapper ────────────────────────────────────────────────────────────

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

const INPUT_CLASS =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent placeholder:text-slate-400';
